import {
  CancelIcon,
  CommonPopupCrud, customUseEffect, FormGroup,
  Grid,
  IconButton,
  MyTextInput,
  Popover,
  textChecker,
  textFieldErr,
  Typography
} from "@common";
import { updateAttachment } from "@features";
import { useFormik } from "formik";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import {
  buildAcceptString,
  normalizeExtensions,
  validateAttachmentFile,
} from "./utils/attachmentValidation";

const PREFIX = "UpdateAttachment";

const classes = {
  popoverPaper: `${PREFIX}-popoverPaper`,
};

const validationSchema = Yup.object({
  description: Yup.string()
    .test(
      "noEmptySpaces",
      " Field cannot be only empty spaces",
      function (value) {
        return value === undefined || value.trim().length > 0;
      }
    )
    .max(255, "Description should not exceed more than 255 characters")
    .matches(textChecker, textFieldErr),
});

const UpdatePage = ({
  showUpdate,
  close,
  getFunction,
  selectedData = {},
  forCampaign,
  forProspect,
  forInquiry,
  forLead,
  forOpportunity,
  forContract,
  forOrder,
  contractId: propContractId,
  orderId: propOrderId,
  allowedFileTypes,
  ...props
}) => {
  const validExtensions = useMemo(
    () => normalizeExtensions(allowedFileTypes || props?.allowedFileTypes),
    [allowedFileTypes, props?.allowedFileTypes]
  );
  const acceptString = useMemo(
    () => buildAcceptString(validExtensions),
    [validExtensions]
  );

  const initialFileName =
    selectedData?.fileName || selectedData?.file || "";
  const [selectedFileName, setSelectedFileName] = useState(
    initialFileName
  );
  const [file, setFile] = useState(null);
  const [fileErr, setFileErr] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [specificFileName, setSpecificFileName] = useState("");
  const [key, setKey] = useState(0);

  customUseEffect(() => {
    if (selectedData) {
      const existingName = selectedData?.fileName || selectedData?.file || "";
      setSelectedFileName(existingName);
      setFile(null);
      setFileErr("");
    }
  }, [selectedData]);

  const open = Boolean(anchorEl);
  const dispatch = useDispatch();

  const loading = useSelector((state) => state?.attachment?.loading);

  const campaignId = selectedData?.campaignId || props?.campaignId || null;
  const prospectId =
    selectedData?.prospectId ||
    selectedData?.prospectId ||
    props?.prospectId ||
    null;
  const inquiryId = selectedData?.inquiryId || props?.inquiryId || null;
  const leadId = selectedData?.leadId || props?.leadId || null;
  const opportunityId =
    selectedData?.opportunityId || props?.opportunityId || null;
  const contractId =
    selectedData?.contractId || propContractId || props?.contractId || null;
  const orderId =
    selectedData?.orderId || propOrderId || props?.orderId || null;

  const handlePopoverOpen = (event, nameToDisplay) => {
    setAnchorEl(event.currentTarget);
    setSpecificFileName(nameToDisplay);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const formik = useFormik({
    initialValues: {
      fileName: selectedData?.fileName || selectedData?.file || "",
      description: selectedData?.description || "",
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (!file && !selectedFileName) {
        setFileErr("File is Required");
        return;
      }

      setFileErr("");

      const formData = new FormData();
      formData.append("id", selectedData?.id || "");
      if (campaignId) formData.append("campaignId", campaignId);
      if (prospectId) formData.append("prospectId", prospectId);
      if (inquiryId) formData.append("inquiryId", inquiryId);
      if (leadId) formData.append("leadId", leadId);
      if (opportunityId) formData.append("opportunityId", opportunityId);
      if (contractId) formData.append("contractId", contractId);
      if (orderId) formData.append("orderId", orderId);

      if (file) {
        formData.append("file", file);
      }
      formData.append("description", values.description || "");

      const res = await dispatch(updateAttachment(formData));

      if (
        res?.payload?.data?.ok ||
        res?.payload?.ok ||
        res?.meta?.requestStatus === "fulfilled"
      ) {
        setTimeout(() => {
          close?.();
          getFunction?.();
        }, 500);

        return true;
      }

      return false;
    },
  });

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];

    if (selectedFile) {
      const { isValid, error } = validateAttachmentFile(selectedFile, validExtensions);
      if (!isValid) {
        setFileErr(error);
        setSelectedFileName(initialFileName);
        setFile(null);
      } else {
        setSelectedFileName(selectedFile.name);
        setFile(selectedFile);
        setFileErr("");
      }

    }

    event.target.value = "";
  };

  const handleClear = () => {
    setSelectedFileName("");
    setFile(null);
    setFileErr("File is Required");
    setKey((prevKey) => prevKey + 1);
  };

  return (
    <CommonPopupCrud
      open={showUpdate}
      heading="Update Attachment"
      handleSubmit={formik?.handleSubmit}
      hideSaveAndNext={true}
      close={close}
      mediumScreen={true}
      loading={loading}
    >
      <form noValidate onSubmit={formik?.handleSubmit}>
        <Grid container>
          <MyTextInput
            label="Description"
            type="text"
            name="description"
            fullWidth={true}
            onBlur={formik.handleBlur}
            margin="normal"
            id="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            error={
              formik.touched.description && Boolean(formik.errors.description)
            }
            helperText={formik.touched.description && formik.errors.description}
          />
        </Grid>
        <Grid style={{ display: "flex", gap: 50 }}>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 4,
            }}
          >
            <FormGroup className="file-browse-btn-div">
              <label className="btn label-sm file-browse-btn">
                Select File *
                <MyTextInput
                  key={key}
                  type="file"
                  name="l1MenuIcon"
                  style={{
                    display: "none",
                    marginTop: "27px",
                    marginLeft: "-28px",
                  }}
                  className="form-control-sm file-input"
                  onChange={handleFileChange}
                  accept={acceptString}
                  inputProps={{
                    accept: acceptString,
                  }}
                  required={true}
                  size="small"
                />
              </label>
            </FormGroup>
          </Grid>
          <Grid
            style={{ display: "flex", alignItems: "center" }}
            size={{
              xs: 12,
              sm: 6,
              md: 8,
            }}
          >

            <p
              onMouseEnter={(event) =>
                handlePopoverOpen(event, selectedFileName)
              }
              onMouseLeave={handlePopoverClose}
            >
              {" "}
              {selectedFileName?.length > 40
                ? selectedFileName.slice(0, 40) + "...."
                : selectedFileName}
            </p>
            {selectedFileName && (
              <IconButton onClick={handleClear}>
                <CancelIcon color="error" />
              </IconButton>
            )}
          </Grid>

          <Popover
            classes={{
              paper: classes.popoverPaper,
            }}
            id="mouse-over-popover"
            sx={{
              pointerEvents: "none",
            }}
            open={open}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            onClose={handlePopoverClose}
            disableRestoreFocus
          >
            <Typography sx={{ p: 1 }}> {specificFileName}</Typography>
          </Popover>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 12,
            md: 12,
          }}
        >
          {fileErr && <p className="formik_err">{fileErr}</p>}
        </Grid>
      </form>
    </CommonPopupCrud>
  );
};

export default UpdatePage;
